import { ReactNode, useState } from "react";
import {
  Box,
  BoxProps,
  Collapse,
  Text,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
import LabeledValue from "components/Shared/LabeledValue";
import Well from "components/Shared/Well";
import EmptyPlaceholder from "components/Shared/EmptyPlaceholder";
import CollapseToggleButton, {
  CollapseToggleIcon,
} from "components/Shared/CollapseToggleButton";
import { formatInteger } from "utils/formatting";

const WARN_SHORT_NAME = 30;
const MAX_SHORT_NAME = 40;

function PlaceholderName(props: BoxProps) {
  return <EmptyPlaceholder opacity={0.6} fontWeight={400} {...props} />;
}

type Props = {
  children?: ReactNode;
  internalSkuId?: string | null;
  isInternalSkuIdAppended?: boolean;
  isLoading?: boolean;
  nameSeparator?: string | null;
  productLineName?: string | null;
  productLineShortName?: string | null;
  skuName?: string | null;
  skuShortName?: string | null;
};

function CharacterCount({
  internalSkuId,
  isInternalSkuIdAppended,
  nameSeparator,
  productLineName,
  skuName,
}: Pick<
  Props,
  | "internalSkuId"
  | "isInternalSkuIdAppended"
  | "nameSeparator"
  | "productLineName"
  | "skuName"
>) {
  let name = `${productLineName}${nameSeparator} ${skuName}`;

  if (isInternalSkuIdAppended && internalSkuId) {
    name += ` (${internalSkuId})`;
  }

  const len = name.length;

  return (
    <Tooltip
      label={
        `Names longer than ${formatInteger(
          MAX_SHORT_NAME,
        )} characters can be ` +
        "truncated or difficult to read in space-constrained areas like charts. " +
        `Where possible, keep names below ${formatInteger(
          WARN_SHORT_NAME,
        )} characters.`
      }
      hasArrow
      openDelay={500}
    >
      <Box>
        <Box
          as="span"
          fontWeight={500}
          data-status={
            (len > MAX_SHORT_NAME && "error") ||
            (len > WARN_SHORT_NAME && "warning")
          }
          sx={{
            "&[data-status=error]": { color: "red" },
            "&[data-status=warning]": { color: "orange.500" },
          }}
        >
          {formatInteger(len)}
        </Box>{" "}
        / {formatInteger(MAX_SHORT_NAME)} characters
      </Box>
    </Tooltip>
  );
}

export default function NamingOptions({
  children,
  internalSkuId,
  isInternalSkuIdAppended = false,
  isLoading,
  nameSeparator = ":",
  productLineName,
  productLineShortName: productLineShortNameProp,
  skuName,
  skuShortName: skuShortNameProp,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  const productLineShortName = productLineShortNameProp || productLineName;
  const skuShortName = skuShortNameProp || skuName;

  const isShortNamePresent = !!(productLineShortNameProp || skuShortNameProp);

  const internalSkuIdDisplay =
    internalSkuId === undefined ? (
      <PlaceholderName>ABC123</PlaceholderName>
    ) : (
      internalSkuId
    );
  const appendedInternalSkuId = isInternalSkuIdAppended &&
    !!internalSkuIdDisplay && (
      <>
        {" ("}
        {internalSkuIdDisplay})
      </>
    );

  return (
    <Well
      borderWidth={1}
      backgroundColor="gray.200"
      borderColor="gray.300"
      borderRadius="md"
      color="gray.700"
      p={0}
      data-expanded={isExpanded}
      sx={{ "&[data-expanded=false]": { boxShadow: "none" } }}
      mb={6}
    >
      <CollapseToggleButton
        isExpanded={isExpanded}
        onExpandedChange={setIsExpanded}
        display="flex"
        justifyContent="space-between"
        variant="unstyled"
        width="100%"
        color="magenta.veryDark"
        px={3}
        py={2}
        fontSize="sm"
        borderRadius="md"
        textTransform="none"
        _hover={{ color: "magenta.dark" }}
      >
        <Box fontFamily="heading">Naming Options</Box>
        <CollapseToggleIcon fontSize=".75em" />
      </CollapseToggleButton>

      <Collapse in={isExpanded}>
        <Box px={3} pb={2}>
          <Box fontSize="xs" color="gray.600" lineHeight="normal">
            <Text>
              The name of the SKU is prefixed by the name of the SKU’s product
              line and a separator where a single name is used to identify SKUs.
              These names are primarily used in charts and certain tables, but
              can appear elsewhere as appropriate.
            </Text>
            <Text my={3}>
              In many cases, the full names of the product line and SKU result
              in names that are too long to be used comfortably in places where
              space is limited. If this is the case, optional shorter names may
              be provided.
            </Text>
          </Box>

          {children}

          <Divider borderColor="gray.300" my={4} />

          <LabeledValue
            label="Full Name"
            isLoaded={
              (typeof productLineName !== "undefined" &&
                typeof skuName !== "undefined") ||
              !isLoading
            }
            value={
              <>
                {productLineName || (
                  <PlaceholderName>Product Line</PlaceholderName>
                )}
                {nameSeparator}{" "}
                {skuName || <PlaceholderName>SKU Name</PlaceholderName>}
                {appendedInternalSkuId}
              </>
            }
            secondaryValue={
              !isShortNamePresent &&
              productLineName &&
              skuName && (
                <CharacterCount
                  {...{
                    internalSkuId,
                    isInternalSkuIdAppended,
                    nameSeparator,
                    productLineName,
                    skuName,
                  }}
                />
              )
            }
            valueProps={{
              flexFlow: "column",
              sx: { "> small": { ml: 0 } },
            }}
            py={0}
          />

          {isShortNamePresent && (
            <LabeledValue
              label="Short Name"
              isLoaded={
                (typeof productLineShortNameProp !== "undefined" &&
                  typeof skuShortNameProp !== "undefined") ||
                !isLoading
              }
              value={
                <>
                  {productLineShortName || (
                    <PlaceholderName>Line</PlaceholderName>
                  )}
                  {nameSeparator}{" "}
                  {skuShortName || <PlaceholderName>SKU</PlaceholderName>}
                  {appendedInternalSkuId}
                </>
              }
              secondaryValue={
                productLineShortName &&
                skuShortName && (
                  <CharacterCount
                    {...{
                      internalSkuId,
                      isInternalSkuIdAppended,
                      nameSeparator,
                    }}
                    productLineName={productLineShortName}
                    skuName={skuShortName}
                  />
                )
              }
              valueProps={{
                flexFlow: "column",
                sx: { "> small": { ml: 0 } },
              }}
              py={0}
              mt={2}
            />
          )}
        </Box>
      </Collapse>
    </Well>
  );
}
